import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  FileCheck, AlertTriangle, CheckCircle, Loader2,
  Shield, Users, Heart, Coins
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

// Helper to get API URL - uses current origin in production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl();

const CharterAcceptanceModal = ({ isOpen, onClose, onAccepted, user, canClose = true }) => {
  const { i18n } = useTranslation();
  const [accepted, setAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charterStatus, setCharterStatus] = useState(null);
  const [guideType, setGuideType] = useState('');
  const [creatorTypes, setCreatorTypes] = useState([]);

  useEffect(() => {
    if (isOpen && user?.role === 'explorer') {
      checkCharterStatus();
    }
  }, [isOpen, user]);

  const checkCharterStatus = async () => {
    try {
      const token = localStorage.getItem('vgt_token');
      const response = await fetch(`${API_URL}/api/explorer/charter-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setCharterStatus(data);
      
      if (data.charter_accepted) {
        onAccepted?.();
      }
    } catch (error) {
      console.error('Error checking charter status:', error);
    }
  };

  const handleAcceptCharter = async () => {
    if (!accepted || !termsAccepted) {
      toast.error(i18n.language === 'fr' 
        ? 'Veuillez accepter la charte et les conditions d\'utilisation'
        : 'Please accept the charter and terms of use');
      return;
    }

    if (!guideType) {
      toast.error(i18n.language === 'fr' 
        ? 'Veuillez choisir votre mode d\'activité'
        : 'Please choose your activity mode');
      return;
    }

    if (creatorTypes.length === 0) {
      toast.error(i18n.language === 'fr' 
        ? 'Veuillez choisir au moins un type d\'expérience'
        : 'Please choose at least one experience type');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('vgt_token');
      const response = await fetch(`${API_URL}/api/explorer/accept-charter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          accepted: true, 
          charter_version: '1.0',
          guide_type: guideType,
          creator_types: creatorTypes
        })
      });

      if (response.ok) {
        toast.success(i18n.language === 'fr' 
          ? 'Charte acceptée ! Bienvenue dans la communauté XPerience !'
          : 'Charter accepted! Welcome to the XPerience community!');
        onAccepted?.();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // If charter already accepted, don't show modal
  if (charterStatus?.charter_accepted) {
    return null;
  }

  const rules = [
    { icon: CheckCircle, text: i18n.language === 'fr' ? 'Je suis sérieux' : 'I am serious', color: 'text-green-600' },
    { icon: Shield, text: i18n.language === 'fr' ? 'Je donne des informations vraies' : 'I provide accurate information', color: 'text-blue-600' },
    { icon: Users, text: i18n.language === 'fr' ? 'Je suis disponible et courtois' : 'I am available and courteous', color: 'text-purple-600' },
    { icon: Heart, text: i18n.language === 'fr' ? 'Je m\'engage à honorer mes prestations avec bienveillance' : 'I commit to delivering my services with kindness', color: 'text-red-500' }
  ];

  const toggleCreatorType = (typeId) => {
    setCreatorTypes(prev => {
      if (prev.includes(typeId)) {
        return prev.filter(t => t !== typeId);
      } else {
        return [...prev, typeId];
      }
    });
  };

  const creatorTypeOptions = [
    { id: 'guide', icon: '🧭', label: i18n.language === 'fr' ? 'Guide local' : 'Local Guide', desc: i18n.language === 'fr' ? 'Visites, randonnées, tours' : 'Visits, hikes, tours' },
    { id: 'artisan', icon: '🎨', label: i18n.language === 'fr' ? 'Artisan' : 'Artisan', desc: i18n.language === 'fr' ? 'Ateliers créatifs' : 'Creative workshops' },
    { id: 'facilitator', icon: '🧘', label: i18n.language === 'fr' ? 'Facilitateur' : 'Facilitator', desc: i18n.language === 'fr' ? 'Retraites, bien-être' : 'Retreats, wellness' },
    { id: 'organizer', icon: '🎪', label: i18n.language === 'fr' ? 'Organisateur' : 'Organizer', desc: i18n.language === 'fr' ? 'Événements, festivals' : 'Events, festivals' }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={canClose ? onClose : () => {}}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={canClose ? undefined : (e) => e.preventDefault()} onEscapeKeyDown={canClose ? undefined : (e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileCheck className="w-6 h-6 text-primary" />
            {i18n.language === 'fr' ? 'Charte des Guides XPerience' : 'XPerience Guide Charter'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Welcome Message */}
          <div className="text-center">
            <span className="text-4xl mb-2 block">👋</span>
            <h2 className="text-lg font-bold text-gray-800">
              {i18n.language === 'fr' 
                ? 'Bienvenue dans la communauté XPerience !'
                : 'Welcome to the XPerience community!'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {i18n.language === 'fr'
                ? 'Guides, artisans, praticiens bien-être, organisateurs d\'événements...'
                : 'Guides, artisans, wellness practitioners, event organizers...'}
            </p>
          </div>

          {/* Introduction */}
          <div className="bg-orange-50 border-l-4 border-primary p-4 rounded-r-lg text-sm">
            <p className="mb-2">
              {i18n.language === 'fr'
                ? 'Tu crées un profil avec photo si possible. Tu établis une liste de choses que tu sais faire découvrir, que ce soit touristique ou pas.'
                : 'Create a profile with a photo if possible. List the things you can help people discover, whether touristic or not.'}
            </p>
            <p className="font-medium text-primary mb-0">
              {i18n.language === 'fr'
                ? 'Bref, tu gères, et je suis sûr que tu sauras surprendre tes hôtes !'
                : 'In short, you\'ve got this, and I\'m sure you\'ll surprise your guests!'}
            </p>
          </div>

          {/* FREE FOR GUIDES */}
          <div className="bg-green-50 border border-green-300 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">🎁</span>
              <strong className="text-green-700">
                {i18n.language === 'fr' ? '100% GRATUIT pour toi' : '100% FREE for you'}
              </strong>
            </div>
            <ul className="text-xs text-green-800 space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-600" />
                {i18n.language === 'fr' ? 'Inscription gratuite' : 'Free registration'}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-600" />
                {i18n.language === 'fr' ? 'Publication gratuite' : 'Free publication'}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-600" />
                {i18n.language === 'fr' ? '15% commission seulement sur les ventes' : '15% commission only on sales'}
              </li>
            </ul>
          </div>

          {/* Business Models */}
          <div className="bg-gradient-to-r from-primary/10 to-green-50 p-4 rounded-xl border border-primary/20">
            <h3 className="text-sm font-bold text-gray-800 mb-3">
              {i18n.language === 'fr' ? '📋 Trois façons de partager :' : '📋 Three ways to share:'}
            </h3>
            
            <div className="space-y-2 text-xs">
              <div className="bg-white p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span>💰</span>
                  <strong className="text-primary">
                    {i18n.language === 'fr' ? 'Expériences payantes' : 'Paid experiences'}
                  </strong>
                </div>
                <p className="text-gray-600 mb-0">
                  {i18n.language === 'fr'
                    ? 'Tu fixes ton prix. Commission de 15% pour la plateforme. Inscription gratuite !'
                    : 'You set your price. 15% commission for the platform. Free registration!'}
                </p>
              </div>
              
              <div className="bg-white p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span>🤝</span>
                  <strong className="text-green-600">
                    {i18n.language === 'fr' ? 'Rencontres gratuites' : 'Free meetups'}
                  </strong>
                </div>
                <p className="text-gray-600 mb-0">
                  {i18n.language === 'fr'
                    ? 'Tu partages par passion, sans frais pour toi. Réservé aux membres XPerience (49€/an).'
                    : 'Share your passion, no fees for you. Reserved for XPerience members (€49/year).'}
                </p>
              </div>
              
              <div className="bg-white p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span>🎪</span>
                  <strong className="text-purple-600">
                    {i18n.language === 'fr' ? 'Événements locaux' : 'Local events'}
                  </strong>
                </div>
                <p className="text-gray-600 mb-0">
                  {i18n.language === 'fr'
                    ? 'Festivals, fêtes locales, marchés : Publication 100% gratuite !'
                    : 'Festivals, local fairs, markets: 100% free publication!'}
                </p>
              </div>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3 text-sm">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="mb-0 text-amber-800">
              {i18n.language === 'fr'
                ? 'La plateforme peut bloquer ou supprimer des utilisateurs qui ne respectent pas les règles.'
                : 'The platform may block or remove users who do not follow the rules.'}
            </p>
          </div>

          {/* Rules */}
          <div>
            <h3 className="text-sm font-bold text-gray-800 mb-3">
              {i18n.language === 'fr' ? 'Je m\'engage à :' : 'I commit to:'}
            </h3>
            <div className="grid gap-2">
              {rules.map((rule, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg text-sm"
                >
                  <rule.icon className={`w-5 h-5 ${rule.color} flex-shrink-0`} />
                  <span className="text-gray-700">{rule.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Creator Type Selection - Multiple */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-bold text-gray-800 mb-2">
              {i18n.language === 'fr' ? 'Quel type d\'expérience proposez-vous ?' : 'What type of experience do you offer?'}
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              {i18n.language === 'fr' 
                ? 'Vous pouvez sélectionner plusieurs types (1 à 3 choix)'
                : 'You can select multiple types (1 to 3 choices)'}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {creatorTypeOptions.map((type) => {
                const isSelected = creatorTypes.includes(type.id);
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => toggleCreatorType(type.id)}
                    className={`p-3 rounded-xl border-2 transition-all text-center ${
                      isSelected 
                        ? 'border-primary bg-primary/10 shadow-md' 
                        : 'border-gray-200 hover:border-primary/50 bg-white'
                    }`}
                  >
                    <span className="text-2xl block mb-1">{type.icon}</span>
                    <span className="font-bold text-xs block">{type.label}</span>
                    <span className="text-xs text-gray-500">{type.desc}</span>
                    {isSelected && (
                      <CheckCircle className="w-4 h-4 text-primary mx-auto mt-1" />
                    )}
                  </button>
                );
              })}
            </div>
            {creatorTypes.length > 0 && (
              <p className="text-xs text-primary mt-2 text-center">
                {creatorTypes.length} {i18n.language === 'fr' 
                  ? (creatorTypes.length === 1 ? 'type sélectionné' : 'types sélectionnés')
                  : (creatorTypes.length === 1 ? 'type selected' : 'types selected')}
              </p>
            )}
          </div>

          {/* Guide Type Selection */}
          <div>
            <h3 className="text-sm font-bold text-gray-800 mb-3">
              {i18n.language === 'fr' ? 'Mon mode d\'activité' : 'My activity mode'}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {/* Paid Guide */}
              <button
                type="button"
                onClick={() => setGuideType('retribution')}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  guideType === 'retribution' 
                    ? 'border-orange-500 bg-orange-50 shadow-md' 
                    : 'border-gray-200 hover:border-orange-300 bg-white'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    guideType === 'retribution' ? 'bg-orange-200' : 'bg-gray-100'
                  }`}>
                    <Coins className={`w-5 h-5 ${guideType === 'retribution' ? 'text-orange-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <span className="font-bold text-sm block">
                      {i18n.language === 'fr' ? 'Contre rétribution' : 'For payment'}
                    </span>
                    <span className="text-xs text-orange-600">
                      {i18n.language === 'fr' ? 'Expériences payantes' : 'Paid experiences'}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600">
                  {i18n.language === 'fr'
                    ? 'Je fixe mes prix. Commission de 15%.'
                    : 'I set my prices. 15% commission.'}
                </p>
                {guideType === 'retribution' && (
                  <div className="mt-2 flex items-center gap-1 text-orange-600 text-xs">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">
                      {i18n.language === 'fr' ? 'Sélectionné' : 'Selected'}
                    </span>
                  </div>
                )}
              </button>

              {/* Passion Guide */}
              <button
                type="button"
                onClick={() => setGuideType('passion')}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  guideType === 'passion' 
                    ? 'border-green-500 bg-green-50 shadow-md' 
                    : 'border-gray-200 hover:border-green-300 bg-white'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    guideType === 'passion' ? 'bg-green-200' : 'bg-gray-100'
                  }`}>
                    <Heart className={`w-5 h-5 ${guideType === 'passion' ? 'text-green-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <span className="font-bold text-sm block">
                      {i18n.language === 'fr' ? 'Par passion' : 'For passion'}
                    </span>
                    <span className="text-xs text-green-600">
                      {i18n.language === 'fr' ? 'Expériences gratuites' : 'Free experiences'}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600">
                  {i18n.language === 'fr'
                    ? 'Je partage bénévolement. Donations volontaires.'
                    : 'I share voluntarily. Voluntary donations.'}
                </p>
                {guideType === 'passion' && (
                  <div className="mt-2 flex items-center gap-1 text-green-600 text-xs">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">
                      {i18n.language === 'fr' ? 'Sélectionné' : 'Selected'}
                    </span>
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="accept-charter"
                checked={accepted}
                onCheckedChange={setAccepted}
                className="mt-1"
              />
              <label htmlFor="accept-charter" className="text-sm cursor-pointer">
                {i18n.language === 'fr'
                  ? <><strong>J'ai lu et j'accepte</strong> la Charte des Guides XPerience et je m'engage à respecter ces valeurs.</>
                  : <><strong>I have read and accept</strong> the XPerience Guide Charter and commit to respecting these values.</>}
              </label>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="accept-terms"
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
                className="mt-1"
              />
              <label htmlFor="accept-terms" className="text-sm cursor-pointer">
                {i18n.language === 'fr'
                  ? <><strong>J'ai lu et j'accepte</strong> les <a href="/terms" target="_blank" className="text-primary hover:underline">Conditions Générales d'Utilisation</a> et la <a href="/privacy" target="_blank" className="text-primary hover:underline">Politique de confidentialité</a>.</>
                  : <><strong>I have read and accept</strong> the <a href="/terms" target="_blank" className="text-primary hover:underline">Terms of Use</a> and <a href="/privacy" target="_blank" className="text-primary hover:underline">Privacy Policy</a>.</>
                }
              </label>
            </div>
          </div>

          {/* Validation Message */}
          {(!accepted || !termsAccepted || creatorTypes.length === 0 || !guideType) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-amber-700 text-xs font-medium mb-1">
                {i18n.language === 'fr' ? 'Pour continuer, veuillez :' : 'To continue, please:'}
              </p>
              <ul className="text-amber-600 text-xs space-y-1">
                {creatorTypes.length === 0 && (
                  <li>• {i18n.language === 'fr' ? 'Choisir au moins un type d\'expérience' : 'Choose at least one experience type'}</li>
                )}
                {!guideType && (
                  <li>• {i18n.language === 'fr' ? 'Choisir votre mode d\'activité' : 'Choose your activity mode'}</li>
                )}
                {!accepted && (
                  <li>• {i18n.language === 'fr' ? 'Cocher la case "J\'ai lu et j\'accepte la Charte"' : 'Check the "I have read and accept the Charter" box'}</li>
                )}
                {!termsAccepted && (
                  <li>• {i18n.language === 'fr' ? 'Cocher la case "J\'ai lu et j\'accepte les CGU"' : 'Check the "I have read and accept the Terms" box'}</li>
                )}
              </ul>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            {canClose && (
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={loading}
              >
                {i18n.language === 'fr' ? 'Plus tard' : 'Later'}
              </Button>
            )}
            <Button
              onClick={handleAcceptCharter}
              disabled={!accepted || !termsAccepted || !guideType || creatorTypes.length === 0 || loading}
              className={canClose ? "flex-1" : "w-full"}
              data-testid="accept-charter-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {i18n.language === 'fr' ? 'Accepter et rejoindre' : 'Accept and join'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CharterAcceptanceModal;
